import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  FileBarChart2,
  RefreshCw,
  Download,
  CheckCircle2,
  AlertTriangle,
  Info,
  ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Project Manager → Report generation — the consolidated replacement for
 * the retired Weekly Digest destination.
 *
 * Local state + hand-written illustrative data only. Nothing here reads
 * real platform analytics, calls a backend, or persists anything — every
 * preview resets when the dialog closes or the page refreshes.
 */

const REPORT_TYPES = [
  { id: 'weekly_performance', label: 'Weekly Performance' },
  { id: 'goals_strategies', label: 'Goals & Strategies' },
  { id: 'projects_tasks', label: 'Projects & Tasks' },
  { id: 'growth_intelligence', label: 'Growth Intelligence' },
  { id: 'milestones_rewards', label: 'Milestones & Rewards' },
];

const PERIODS = ['This Week', 'Last Week', 'This Month', 'Last Month', 'Custom Date Range'];

// Small, clearly-illustrative dataset per report type. Kept intentionally
// separate from ProjectPlanningDemo's local state (not authorized to
// modify that file to expose it) — these numbers are sample content only.
const REPORT_CONTENT = {
  weekly_performance: {
    title: 'Weekly Performance Report',
    executiveSummary: 'Publishing stayed consistent this period with steady audience growth. Revenue tracked slightly ahead of plan while engagement held flat — worth a closer look next week.',
    kpis: [
      { label: 'Content Published', value: '9 pieces' },
      { label: 'Audience Growth', value: '+1,240 (sample)' },
      { label: 'Engagement Rate', value: '4.8% (sample)' },
      { label: 'Revenue', value: '$640 (sample)' },
      { label: 'Consistency Score', value: '82/100 (sample)' },
    ],
    breakdown: [
      { label: 'Content Published', value: '9 pieces across YouTube, TikTok, and Instagram (sample).' },
      { label: 'Audience Growth', value: '+1,240 new followers this period (sample).' },
      { label: 'Engagement', value: '4.8% average engagement rate, roughly flat vs. prior period (sample).' },
      { label: 'Revenue', value: '$640 in tracked sponsorship + ad revenue (sample).' },
      { label: 'Consistency', value: 'Consistency Score held at 82/100 — 3 of 4 planned uploads landed on schedule (sample).' },
    ],
    highlights: ['Three short-form videos outperformed your 90-day average (sample).', 'Livestream attendance grew for the second week running (sample).'],
    attention: ['Engagement rate has been flat for two periods — consider refreshing hooks/CTAs (sample).', 'One scheduled upload slipped by two days (sample).'],
    actions: ['Test two new short-form hook styles next week (sample).', 'Re-confirm the livestream guest booking earlier in the week (sample).'],
  },
  goals_strategies: {
    title: 'Goals & Strategies Report',
    executiveSummary: 'Revenue and audience goals are both progressing, with the Short-Form Blitz strategy on pace to hit its weekly output target.',
    kpis: [
      { label: 'Active Goals', value: '2 (sample)' },
      { label: 'Avg. Goal Progress', value: '58% (sample)' },
      { label: 'Active Strategies', value: '1 (sample)' },
      { label: 'On-Pace Strategies', value: '1 of 1 (sample)' },
    ],
    breakdown: [
      { label: 'Active Goals', value: '"Reach $5,000/month" (64% of target) and "Grow to 50K subscribers" (77% of target) (sample).' },
      { label: 'Goal Progress', value: 'Both goals trending upward period-over-period (sample).' },
      { label: 'Strategy Execution', value: '"Short-Form Blitz" delivered 18 of a planned 20 shorts this week (sample).' },
      { label: 'Targets vs. Results', value: 'Slightly under the 20/week short-form target; livestream cadence met in full (sample).' },
      { label: 'Recommended Adjustments', value: 'Consider batching short-form filming earlier in the week to close the output gap (sample).' },
    ],
    highlights: ['Revenue goal crossed the 60% mark for the first time (sample).'],
    attention: ['Short-form output came in 2 videos under the weekly strategy target (sample).'],
    actions: ['Add one batch-filming day to the production calendar (sample).', 'Review whether the 20/week target is still realistic (sample).'],
  },
  projects_tasks: {
    title: 'Projects & Tasks Report',
    executiveSummary: 'Production is moving through the pipeline with one project nearing completion and a small backlog of overdue tasks worth clearing.',
    kpis: [
      { label: 'Active Projects', value: '2 (sample)' },
      { label: 'Tasks Completed', value: '4 (sample)' },
      { label: 'Tasks Overdue', value: '1 (sample)' },
      { label: 'Avg. Production Progress', value: '68% (sample)' },
    ],
    breakdown: [
      { label: 'Active Projects', value: '"Q1 Sponsor Campaign" (Editing, 55%) and "Weekly Livestream Series" (Scheduling, 80%) (sample).' },
      { label: 'Completed Tasks', value: '4 manually-tracked tasks marked done this period (sample).' },
      { label: 'Overdue Tasks', value: '1 task ("Confirm livestream guest") passed its due date (sample).' },
      { label: 'Production Progress', value: 'Both active projects advanced at least one production stage this period (sample).' },
      { label: 'Upcoming Deadlines', value: '"Q1 Sponsor Campaign" is due in 6 days (sample).' },
    ],
    highlights: ['"Weekly Livestream Series" reached the Scheduling stage ahead of its due date (sample).'],
    attention: ['1 task is overdue and needs a new owner or due date (sample).'],
    actions: ['Reassign or reschedule the overdue guest-booking task today (sample).', 'Start Optimization planning for the Sponsor Campaign now that Editing is near done (sample).'],
  },
  growth_intelligence: {
    title: 'Growth Intelligence Report',
    executiveSummary: 'Growth is in a steady "Momentum" stage. Audience trends are positive, though one recurring bottleneck is limiting faster gains.',
    kpis: [
      { label: 'Growth Stage', value: 'Momentum (sample)' },
      { label: 'Audience Trend', value: 'Upward (sample)' },
      { label: 'Top Bottleneck', value: 'Publishing cadence (sample)' },
      { label: 'Priority Items', value: '3 (sample)' },
    ],
    breakdown: [
      { label: 'Growth Stage', value: 'Currently in the "Momentum" stage of the growth flywheel (sample).' },
      { label: 'Audience Trends', value: 'Subscriber growth trending up for 3 consecutive periods (sample).' },
      { label: 'Performance Bottlenecks', value: 'Inconsistent publishing cadence is the most-cited limiting factor (sample).' },
      { label: 'Recommended Priorities', value: 'Tighten the weekly content calendar; double down on the best-performing short-form format (sample).' },
    ],
    highlights: ['Momentum score improved for the second period in a row (sample).'],
    attention: ['Publishing cadence remains the top-ranked bottleneck (sample).'],
    actions: ['Lock a fixed weekly publishing calendar for the next 4 weeks (sample).'],
  },
  milestones_rewards: {
    title: 'Milestones & Rewards Report',
    executiveSummary: 'Personal milestones are progressing well, and simulated Rewards Progress shows you are on pace for your next tier.',
    kpis: [
      { label: 'Personal Milestones', value: '2 (sample)' },
      { label: 'Avg. Milestone Progress', value: '58% (sample)' },
      { label: 'Rewards Tier Progress', value: '67% (simulated, from Growth Engine)' },
    ],
    breakdown: [
      { label: 'Personal Milestone Progress', value: '"Publish 20 clips this week" at 13/20; "Complete 4 episodes this month" at 2/4 (sample).' },
      { label: 'Rewards Progress (Simulated)', value: 'Reflects the existing simulated Rewards Progress shown in Project Management — not a real rewards calculation.' },
      { label: 'Recent Achievements', value: 'Completed first livestream milestone of the month (sample).' },
    ],
    highlights: ['Clip-publishing milestone is more than halfway complete (sample).'],
    attention: ['Episode-completion milestone is behind pace for the month (sample).'],
    actions: ['Schedule the remaining 2 episodes before month end (sample).'],
  },
};

function todayLabel() {
  return new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export const ProjectReportsDemo = ({ open, onOpenChange, defaultReportType }) => {
  const [reportType, setReportType] = useState(defaultReportType || 'weekly_performance');
  const [period, setPeriod] = useState('This Week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [preview, setPreview] = useState(null);

  // Keep the config in sync if a new default arrives (e.g. opened via the
  // legacy /app/account/weekly-digest → ?report=weekly link) without
  // clobbering a choice the user already made mid-session.
  useEffect(() => {
    if (open && defaultReportType && !preview) {
      setReportType(defaultReportType);
    }
  }, [open, defaultReportType]);

  const periodLabel = period === 'Custom Date Range' && customStart && customEnd
    ? `${customStart} to ${customEnd}`
    : period;

  const generatePreview = () => {
    if (period === 'Custom Date Range') {
      if (!customStart || !customEnd) {
        toast.error('Enter both a start and end date for a custom range');
        return;
      }
      if (new Date(customStart) > new Date(customEnd)) {
        toast.error('Start date must be before end date');
        return;
      }
    }
    const content = REPORT_CONTENT[reportType];
    setPreview({
      ...content,
      period: periodLabel,
      generatedDate: todayLabel(),
    });
    toast.success('Preview generated from sample data (demo only)');
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) setPreview(null);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#14171F] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="project-reports-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileBarChart2 className="h-4 w-4 text-cyan-400" />
            Generate Report
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[#8A93A6] flex items-center gap-1.5 -mt-1" data-testid="reports-demo-notice">
          <Info className="h-3 w-3 shrink-0" />
          Demo only — built from sample illustrative data, not verified platform analytics. Nothing here is saved.
        </p>

        {/* Configuration */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-[#8A93A6]">Report type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="h-9 text-sm bg-black/40 border-white/10 mt-1" data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-[#8A93A6]">Reporting period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9 text-sm bg-black/40 border-white/10 mt-1" data-testid="report-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {period === 'Custom Date Range' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-[#8A93A6]">Start date</Label>
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 text-sm bg-black/40 border-white/10 mt-1" data-testid="report-custom-start-input" />
            </div>
            <div>
              <Label className="text-[11px] text-[#8A93A6]">End date</Label>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 text-sm bg-black/40 border-white/10 mt-1" data-testid="report-custom-end-input" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button size="sm" onClick={generatePreview} data-testid="generate-preview-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            {preview ? 'Regenerate Preview' : 'Generate Preview'}
          </Button>
          <Button size="sm" variant="outline" className="border-white/10 text-[#8A93A6] cursor-not-allowed" disabled data-testid="export-pdf-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export PDF
            <Badge variant="outline" className="ml-2 text-[9px] border-violet-500/30 text-violet-300 bg-violet-500/10">Coming in Level 3</Badge>
          </Button>
        </div>

        {/* Preview */}
        {preview && (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4" data-testid="report-preview">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-semibold text-white" data-testid="report-preview-title">{preview.title}</h3>
                <p className="text-[11px] text-[#8A93A6] mt-0.5">Reporting period: {preview.period} · Generated {preview.generatedDate}</p>
              </div>
              <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 shrink-0">Sample Data — Demo Only</Badge>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#8A93A6] mb-1">Executive Summary</p>
              <p className="text-sm text-[#C0C7D4] leading-relaxed">{preview.executiveSummary}</p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#8A93A6] mb-2">KPI Summary</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {preview.kpis.map((k) => (
                  <div key={k.label} className="rounded-md border border-white/5 bg-black/30 px-3 py-2">
                    <p className="text-[10px] text-[#8A93A6]">{k.label}</p>
                    <p className="text-sm font-semibold text-white">{k.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#8A93A6] mb-2 flex items-center gap-1.5"><ListChecks className="h-3 w-3" />Report Detail</p>
              <div className="space-y-1.5">
                {preview.breakdown.map((b) => (
                  <p key={b.label} className="text-[12px] text-[#C0C7D4]"><span className="text-white font-medium">{b.label}:</span> {b.value}</p>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-green-300/80 mb-1.5 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" />Performance Highlights</p>
              <ul className="space-y-1">
                {preview.highlights.map((h, i) => <li key={i} className="text-[12px] text-[#C0C7D4] pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-green-400">{h}</li>)}
              </ul>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-amber-300/80 mb-1.5 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" />Areas Requiring Attention</p>
              <ul className="space-y-1">
                {preview.attention.map((a, i) => <li key={i} className="text-[12px] text-[#C0C7D4] pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-amber-400">{a}</li>)}
              </ul>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-cyan-300/80 mb-1.5">Recommended Next Actions</p>
              <ul className="space-y-1">
                {preview.actions.map((a, i) => <li key={i} className="text-[12px] text-[#C0C7D4] pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-cyan-400">{a}</li>)}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button size="sm" variant="outline" className="border-white/10" onClick={() => handleClose(false)} data-testid="close-reports-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectReportsDemo;
